import { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';
import './ContactModal.css';

const SUBJECT_OPTIONS = [
  'Advertisement Enquiry',
  'General Enquiry',
  'Feedback',
  'Complaint',
  'Technical Support',
  'Partnership / Sponsorship',
  'Other',
];

export default function ContactModal({ isOpen, onClose, prefill = {} }) {
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    subject: prefill.subject || 'General Enquiry',
    message: '',
    sendCopy: false,
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  // Reset / sync when modal opens with new prefill
  useEffect(() => {
    if (isOpen) {
      setForm(prev => ({
        ...prev,
        subject: prefill.subject || 'General Enquiry',
        message: prefill.message || '',
      }));
      setStatus('idle');
    }
  }, [isOpen, prefill.subject]);

  // Escape key to close
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    setStatus('sending');

    try {
      // 1. Write to Firestore (admin count widget + audit trail)
      await addDoc(collection(db, 'contact_submissions'), {
        name:        form.name.trim(),
        company:     form.company.trim() || null,
        email:       form.email.trim(),
        subject:     form.subject,
        message:     form.message.trim(),
        sendCopy:    form.sendCopy,
        submittedAt: serverTimestamp(),
        read:        false,
        source:      prefill.source || 'general',
      });

      // 2. Send email via Cloud Function (Resend) — no email client required
      const fns       = getFunctions(undefined, 'australia-southeast1');
      const sendEmail = httpsCallable(fns, 'sendContactFormEmail');
      await sendEmail({
        name:     form.name.trim(),
        company:  form.company.trim() || '',
        email:    form.email.trim(),
        subject:  form.subject,
        message:  form.message.trim(),
        sendCopy: form.sendCopy,
        source:   prefill.source || 'general',
      });

      setStatus('success');

    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="ss-modal-backdrop" onClick={onClose} />
      <div
        className="ss-contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
      >
        <div className="ss-contact-modal-header">
          <h2 id="contact-modal-title">Contact Us</h2>
          <button
            className="ss-contact-modal-close"
            onClick={onClose}
            aria-label="Close"
          >✕</button>
        </div>

        {status === 'success' ? (
          <div className="ss-contact-modal-success">
            <div className="ss-contact-success-icon">✅</div>
            <h3>Message Sent</h3>
            <p>
              We've received your message and will get back to you within
              1–2 business days.
              {form.sendCopy && ' A copy has been sent to your email address.'}
            </p>
            <button className="ss-contact-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : status === 'error' ? (
          <div className="ss-contact-modal-success">
            <div className="ss-contact-success-icon">⚠️</div>
            <h3>Something went wrong</h3>
            <p>
              We couldn't save your message. Please email us directly at{' '}
              <a href="mailto:admin@syndicatorshowdown.com.au">admin@syndicatorshowdown.com.au</a>.
            </p>
            <button className="ss-contact-btn-primary" onClick={() => setStatus('idle')}>
              Try Again
            </button>
          </div>
        ) : (
          <form className="ss-contact-form" onSubmit={handleSubmit} noValidate>
            <div className="ss-contact-form-row">
              <div className="ss-contact-field">
                <label htmlFor="contact-name">Full Name *</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                  autoComplete="name"
                />
              </div>
              <div className="ss-contact-field">
                <label htmlFor="contact-company">Company / Organisation</label>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Optional"
                  autoComplete="organization"
                />
              </div>
            </div>

            <div className="ss-contact-form-row">
              <div className="ss-contact-field">
                <label htmlFor="contact-email">Email Address *</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="ss-contact-field">
                <label htmlFor="contact-subject">Subject *</label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  required
                >
                  {SUBJECT_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ss-contact-field">
              <label htmlFor="contact-message">Message *</label>
              <textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us what's on your mind..."
                rows={5}
                required
              />
            </div>

            <label className="ss-contact-checkbox-label">
              <input
                type="checkbox"
                name="sendCopy"
                checked={form.sendCopy}
                onChange={handleChange}
              />
              <span>Send a copy of this message to my email address</span>
            </label>

            <div className="ss-contact-form-footer">
              <p className="ss-contact-privacy">
                Your details are used only to respond to your enquiry and
                are not shared with third parties.
              </p>
              <div className="ss-contact-form-actions">
                <button
                  type="button"
                  className="ss-contact-btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ss-contact-btn-primary"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Opening…' : 'Send Message →'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
